'use client';

import { c as _c } from "react/compiler-runtime";
import type { DesktopReviewQueue } from '@/components/desktop/review-queue';
import type { WarmedPrDiff } from '@/lib/desktop-pr-warm-up';
import { hashKey, useQueries, useQueryClient } from '@tanstack/react-query';
import { powerMonitor } from '@todesktop/client-core';
import { useEffect, useState } from 'react';
import { getInboxPrPath } from '@/app/(home)/inbox/utils/pull-requests';
import { useDesktopReviewQueue } from '@/components/desktop/use-desktop-review-queue';
import { useIdleCallback } from '@/hooks/use-idle-callback';
import { listenToDesktopEvent } from '@/lib/desktop-app-listener';
import { warmPrData } from '@/lib/desktop-pr-warm-up';
import { useAuth } from '@/providers/auth-provider';
const PRELOADED_PR_COUNT = 8;

/** Plugging in or unlocking ends what held the preload back. */
const RESUME_EVENTS = ['on-ac', 'unlock-screen'] as const;
type WarmedPr = {
  head: string;
  diff: WarmedPrDiff | null;
};

// The inbox warms the diff's highlighting workers too, but this window may sit on another page.
// Imported on demand to keep Pierre out of the shared bundle; a chunk that fails to load skips it.
function warmPrDiffWorkerPool() {
  import('@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/pr-v2-diff-worker-pool').then(({
    warmPrV2DiffWorkerPool
  }) => warmPrV2DiffWorkerPool()).catch(() => {});
}
async function canPreloadNow(): Promise<boolean> {
  const [onBattery, idleState] = await Promise.all([powerMonitor.isOnBatteryPower().catch(() => false), powerMonitor.getSystemIdleState(60).catch(() => 'unknown')]);
  return !onBattery && idleState !== 'locked';
}

/** The first PRs of the queue's open sections, with their head SHAs. */
function getQueueTop(queue: DesktopReviewQueue): Array<{
  href: string;
  head: string;
}> {
  if (queue.status !== 'ready') {
    return [];
  }
  return queue.open.flatMap(group => group.items).slice(0, PRELOADED_PR_COUNT).map(({
    pr
  }) => ({
    href: getInboxPrPath(pr),
    head: pr.head.sha ?? ''
  }));
}

/**
 * Keeps the top of the review queue loaded, one PR at a time, so opening one from the tray shows
 * it at once. Waits while the computer is on battery or locked. A PR loads again when its head
 * changes, which the inbox refetch after a push reports, or when its diff leaves the cache.
 */
export function DesktopQueuePreloader() {
  const $ = _c(20);
  const queryClient = useQueryClient();
  const {
    user
  } = useAuth();
  const queue = useDesktopReviewQueue();
  const [warmedPrs, setWarmedPrs] = useState(_temp);
  const [resumeCount, setResumeCount] = useState(0);
  useIdleCallback(warmPrDiffWorkerPool);
  const queueTop = getQueueTop(queue);
  let t0;
  if ($[0] !== warmedPrs) {
    t0 = t1 => {
      const {
        href,
        head
      } = t1;
      const warmed = warmedPrs.get(href);
      return warmed?.head === head && warmed.diff ? [{
        ...warmed.diff,
        enabled: false
      }] : [];
    };
    $[0] = warmedPrs;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useQueries({
    queries: queueTop.flatMap(t0)
  });
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const resume = () => setResumeCount(_temp2);
      const stopListening = RESUME_EVENTS.map(event => listenToDesktopEvent(`the desktop ${event} event`, powerMonitor.on(event, resume)));
      return () => {
        for (const stop of stopListening) {
          stop();
        }
      };
    };
    t2 = [];
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== queryClient) {
    t3 = () => queryClient.getQueryCache().subscribe(event_0 => {
      if (event_0.type !== "removed") {
        return;
      }
      setWarmedPrs(previous => {
        const kept = [...previous].filter(t5 => {
          const [, t6] = t5;
          const {
            diff
          } = t6;
          return !diff || hashKey(diff.queryKey) !== event_0.query.queryHash;
        });
        return kept.length === previous.size ? previous : new Map(kept);
      });
    });
    t4 = [queryClient];
    $[4] = queryClient;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== warmedPrs) {
    t5 = t6 => {
      const {
        href: href_0,
        head: head_0
      } = t6;
      return warmedPrs.get(href_0)?.head !== head_0;
    };
    $[7] = warmedPrs;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const next = queueTop.find(t5);
  const nextHref = next?.href;
  const nextHead = next?.head;
  let t6;
  if ($[9] !== nextHead || $[10] !== nextHref || $[11] !== queryClient || $[12] !== user) {
    t6 = () => {
      if (!nextHref || !nextHead) {
        return;
      }
      let isCancelled = false;
      (async () => {
        if (!(await canPreloadNow()) || isCancelled) {
          return;
        }
        const diff_0 = await warmPrData(queryClient, nextHref, user);
        if (!isCancelled) {
          setWarmedPrs(previous_0 => new Map(previous_0).set(nextHref, {
            head: nextHead,
            diff: diff_0
          }));
        }
      })();
      return () => {
        isCancelled = true;
      };
    };
    $[9] = nextHead;
    $[10] = nextHref;
    $[11] = queryClient;
    $[12] = user;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== nextHead || $[15] !== nextHref || $[16] !== queryClient || $[17] !== resumeCount || $[18] !== user) {
    t7 = [nextHead, nextHref, queryClient, resumeCount, user];
    $[14] = nextHead;
    $[15] = nextHref;
    $[16] = queryClient;
    $[17] = resumeCount;
    $[18] = user;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  useEffect(t6, t7);
  return null;
}
function _temp2(count) {
  return count + 1;
}
function _temp() {
  return new Map();
}